import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Mapa path → módulo de permisos (para mostrar según la matriz, no el rol)
const PATH_MODULO = {
  '/viajes': 'viajes',
  '/tanques': 'tanques',
  '/vehiculos': 'vehiculos',
  '/conductores': 'conductores',
  '/clientes': 'clientes',
  '/tipos-combustible': 'tipos_combustible',
  '/origenes-carga': 'origenes_carga',
  '/excedentes': 'excedentes',
  '/reportes': 'reportes',
  '/usuarios': 'usuarios',
};

// ¿El usuario puede VER este módulo? (según su matriz de permisos efectiva)
const puedeVer = (usuario, path) => {
  const mod = PATH_MODULO[path];
  if (!mod) return true;                         // rutas sin módulo (ej. Inicio)
  const permisos = usuario?.permisos;
  if (!permisos) {                               // sin matriz cargada → fallback por rol admin
    return usuario?.rol === 'admin';
  }
  return !!(permisos[mod] && permisos[mod].ver);
};

const CATEGORIAS = [
  {
    id: 'operaciones', label: 'Operaciones', icon: '🚛',
    modulos: [
      { path: '/viajes', label: 'Viajes', icon: '🚛' },
      { path: '/tanques', label: 'Tanques', icon: '🛢️' },
    ]
  },
  {
    id: 'catalogos', label: 'Catálogos', icon: '📋',
    modulos: [
      { path: '/vehiculos',         label: 'Vehículos',         icon: '🚗' },
      { path: '/conductores',       label: 'Conductores',       icon: '👤' },
      { path: '/clientes',          label: 'Clientes',          icon: '🏢' },
      { path: '/tipos-combustible', label: 'Combustibles',      icon: '⛽' },
      { path: '/origenes-carga',    label: 'Orígenes de Carga', icon: '📍' },
    ]
  },
  {
    id: 'finanzas', label: 'Finanzas', icon: '💰',
    modulos: [
      { path: '/excedentes', label: 'Excedentes', icon: '💰' },
      { path: '/reportes',   label: 'Reportes',   icon: '📊' },
    ]
  },
  {
    id: 'admin', label: 'Admin', icon: '⚙️',
    modulos: [
      { path: '/usuarios', label: 'Usuarios', icon: '👥' },
    ]
  },
];

const BOTTOM_NAV = [
  { path: '/',           label: 'Inicio',     icon: '🏠' },
  { path: '/viajes',     label: 'Viajes',     icon: '🚛' },
  { path: '/clientes',   label: 'Clientes',   icon: '🏢' },
  { path: '/excedentes', label: 'Excedentes', icon: '💰' },
  { path: '/vehiculos',  label: 'Vehículos',  icon: '🚗' },
];

const ROLES_ES = { admin: 'Administrador', conductor: 'Conductor', despachador: 'Despachador', finanzas: 'Finanzas' };

const Layout = ({ usuario, onLogout, children, title, showDashboard }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const rol = usuario?.rol || 'conductor';
  // Inicio siempre visible; el resto según permiso de ver
  const bottomNav = BOTTOM_NAV.filter(b => b.path === '/' || puedeVer(usuario, b.path));

  return (
    <div style={S.root}>
      <header style={S.header}>
        <div style={S.headerLeft}>
          <div style={S.logoCircle} onClick={() => navigate('/')}>
            {!logoError ? (
              <img
                src="/logo-gr.png"
                alt="GR"
                style={S.logoImg}
                onError={() => setLogoError(true)}
              />
            ) : (
              <span style={S.logoText}>GR</span>
            )}
          </div>
          <div>
            <div style={S.appName}>CombustibleApp</div>
            <div style={S.appSub}>Gerónimo Ramírez SRL</div>
          </div>
        </div>
        <div style={S.headerRight}>
          <div style={S.userBadge} onClick={() => setMenuOpen(!menuOpen)}>
            <span style={S.userInitial}>{usuario?.nombre?.charAt(0)}</span>
            <span style={S.userName}>{ROLES_ES[rol]}</span>
          </div>
          {menuOpen && (
            <div style={S.dropdown}>
              <div style={S.dropdownUser}>
                <div style={S.dropdownName}>{usuario?.nombre}</div>
                <div style={S.dropdownRol}>{ROLES_ES[rol]}</div>
              </div>
              <button style={S.dropdownItem} onClick={() => { setMenuOpen(false); navigate('/cambiar-password'); }}>🔑 Cambiar contraseña</button>
              <button style={S.dropdownLogout} onClick={onLogout}>🚪 Cerrar sesión</button>
            </div>
          )}
        </div>
      </header>
      {menuOpen && <div style={S.overlay} onClick={() => setMenuOpen(false)} />}
      <main style={S.main}>
        {!showDashboard && title && (
          <div style={S.pageHeader}>
            <button style={S.backBtn} onClick={() => navigate('/')}>← Inicio</button>
            <h1 style={S.pageTitle}>{title}</h1>
          </div>
        )}
        <div style={S.content}>{children}</div>
      </main>
      <nav style={S.bottomNav}>
        {bottomNav.map(item => (
          <button
            key={item.path}
            style={{ ...S.bottomBtn, ...(location.pathname === item.path ? S.bottomBtnActive : {}) }}
            onClick={() => navigate(item.path)}
          >
            <span style={S.bottomIcon}>{item.icon}</span>
            <span style={{ ...S.bottomLabel, ...(location.pathname === item.path ? { color: '#CC0000' } : {}) }}>{item.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
};

export { CATEGORIAS, puedeVer };
export default Layout;

const S = {
  root: { display: 'flex', flexDirection: 'column', minHeight: '100vh', background: '#f5f5f5', fontFamily: "'Segoe UI', sans-serif" },
  header: { background: '#1a1a1a', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 2px 8px rgba(0,0,0,0.3)' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '10px' },
  logoCircle: { width: '38px', height: '38px', borderRadius: '50%', background: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, overflow: 'hidden' },
  logoImg: { width: '38px', height: '38px', objectFit: 'contain' },
  logoText: { color: '#CC0000', fontSize: '14px', fontWeight: '900' },
  appName: { color: '#fff', fontSize: '14px', fontWeight: '700', lineHeight: 1.2 },
  appSub: { color: '#CC0000', fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.3px' },
  headerRight: { position: 'relative' },
  userBadge: { display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '6px 10px', borderRadius: '8px', background: 'rgba(255,255,255,0.1)' },
  userInitial: { width: '28px', height: '28px', borderRadius: '50%', background: '#CC0000', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '13px', fontWeight: '700' },
  userName: { color: '#fff', fontSize: '12px', fontWeight: '600' },
  dropdown: { position: 'absolute', top: '44px', right: 0, background: '#fff', borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', minWidth: '180px', overflow: 'hidden', zIndex: 200 },
  dropdownUser: { padding: '14px 16px', borderBottom: '1px solid #f0f0f0' },
  dropdownName: { fontSize: '14px', fontWeight: '700', color: '#1a1a1a' },
  dropdownRol: { fontSize: '12px', color: '#888', marginTop: '2px' },
  dropdownItem: { width: '100%', padding: '12px 16px', border: 'none', borderBottom: '1px solid #f0f0f0', background: 'none', textAlign: 'left', fontSize: '13px', color: '#1a1a1a', fontWeight: '600', cursor: 'pointer' },
  dropdownLogout: { width: '100%', padding: '12px 16px', border: 'none', background: 'none', textAlign: 'left', fontSize: '13px', color: '#CC0000', fontWeight: '600', cursor: 'pointer' },
  overlay: { position: 'fixed', inset: 0, zIndex: 99 },
  main: { flex: 1, paddingBottom: '80px' },
  pageHeader: { background: '#fff', padding: '12px 16px', borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: '12px', position: 'sticky', top: '62px', zIndex: 50 },
  backBtn: { background: 'none', border: 'none', color: '#CC0000', fontSize: '14px', fontWeight: '600', cursor: 'pointer', padding: '4px 8px', borderRadius: '6px' },
  pageTitle: { fontSize: '16px', fontWeight: '700', color: '#1a1a1a', margin: 0 },
  content: { padding: '16px' },
  bottomNav: { position: 'fixed', bottom: 0, left: 0, right: 0, background: '#fff', borderTop: '1px solid #e0e0e0', display: 'flex', zIndex: 100, boxShadow: '0 -2px 8px rgba(0,0,0,0.08)' },
  bottomBtn: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '8px 4px', border: 'none', background: 'none', cursor: 'pointer', gap: '3px' },
  bottomBtnActive: { background: '#fff0f0' },
  bottomIcon: { fontSize: '22px', lineHeight: 1 },
  bottomLabel: { fontSize: '10px', fontWeight: '600', color: '#888' },
};
